<script lang="ts">
    /**
     * DifferentiationSection - Section 2: 핵심 차별화
     *
     * 홈페이지에서 가장 넓은 영역
     * - 2-1: 협동 모드 소개 (플로우 + 걸음수 협동·추격전)
     * - 2-2: 추격 모드 (Chase Mode) — 별도 강조
     *
     * 🚨 앱이 실제로 하는 것만 말한다(2026-09-14, 모바일 local-work 6efdda1 대조).
     *    - 협동 "9가지 타입" 을 뺐다: Android 는 어느 협동 타입을 골라도 진행값이 걸음수 하나로 쌓이고,
     *      iOS 는 7종만 고를 수 있으며 타입마다 측정이 달랐다(거리·칼로리·명상·심박수 측정이 아니다).
     *    - 파워업 3종·도발 보너스·폰 흔들어 포획·포인트/배지 지급은 앱에서 효과가 없거나 없는 기능이라 뺐다.
     *    앱을 고치면 그때 되살린다. 판정 근거는 docs/pin/애드센스-재심사-계획.md 후속 절.
     */

    const challengeTypes = [
        { icon: '👟', name: '걸음수 협동', desc: '팀 걸음수 목표 달성' },
        { icon: '🏃', name: '추격전', desc: '도망자 vs 추격자 게임!' },
    ];

    const flowSteps = [
        { step: '1', title: '챌린지 설정', desc: '호스트·참가자 선택, 타입·목표 설정' },
        { step: '2', title: '근처 기기 탐색', desc: '근처에서 방을 연 호스트를 목록에 표시' },
        { step: '3', title: '대기실', desc: '참가자 목록, 전원 준비 시 시작' },
        { step: '4', title: '챌린지 진행', desc: '실시간 진행률, 타이머, 순위' },
        { step: '5', title: '결과', desc: '최종 순위·결과 확인' },
    ];

    const difficulties = [
        { color: '#4CAF50', name: '캐주얼', prison: false, steps: false, desc: '가볍게 뛰어보기' },
        { color: '#2196F3', name: '클래식', prison: true, steps: false, desc: '감옥 & 구출 추가' },
        { color: '#FFC107', name: '걸음수 챌린지', prison: false, steps: true, desc: '팀 걸음수 목표 3,000걸음' },
        { color: '#F44336', name: '풀 옵션', prison: true, steps: true, desc: '감옥·무적·걸음수 목표까지' },
    ];

</script>

<section id="differentiation" class="diff-section">
    <!-- Section Header -->
    <div class="diff-header">
        <h2 class="diff-title">가까이 있으면, 함께 시작하는 건강 모험</h2>
        <p class="diff-subtitle">
            근처 친구를 자동으로 찾아 주고,<br />
            함께 걷고, 뛰고, 도망치면서 건강해지세요.<br />
            <strong>특허 기술 기반의 차별화된 기능입니다.</strong>
        </p>
    </div>

    <!-- 2-1: 협동 모드 -->
    <div class="diff-block">
        <h3 class="block-title">협동 모드 — 함께 목표를 달성하세요</h3>
        <p class="block-desc">
            걸음수를 팀 목표로 함께 모으고,
            도망자·추격자로 나뉘는 추격전까지 즐겨 보세요.
            실시간으로 서로의 진행률을 확인하며 응원할 수 있어요!
        </p>

        <!-- App Screenshots -->
        <div class="diff-screenshots">
            <!-- 🚨 png·webp 는 한 쌍으로 갱신한다(함정 9). webp 를 지우면 폴백이 아니라 깨진 이미지가 뜬다. -->
            <div class="diff-phone">
                <picture>
                    <source srcset="/app-guide/screenshot/android/Screenshot_20261003_152121.webp" type="image/webp" />
                    <img src="/app-guide/screenshot/android/Screenshot_20261003_152121.png" alt="챌린지 목록 화면"
                        loading="lazy" width="1080" height="2340" />
                </picture>
            </div>
            <div class="diff-phone">
                <picture>
                    <source srcset="/app-guide/screenshot/android/Screenshot_20261003_152137.webp" type="image/webp" />
                    <img src="/app-guide/screenshot/android/Screenshot_20261003_152137.png" alt="근거리 챌린지 상세 화면"
                        loading="lazy" width="1080" height="2340" />
                </picture>
            </div>
        </div>

        <!-- Flow Steps -->
        <div class="flow-steps">
            {#each flowSteps as step, i}
                <div class="flow-step">
                    <div class="flow-number">{step.step}</div>
                    <div class="flow-title">{step.title}</div>
                    <div class="flow-desc">{step.desc}</div>
                </div>
                {#if i < flowSteps.length - 1}
                    <div class="flow-arrow">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                        </svg>
                    </div>
                {/if}
            {/each}
        </div>

        <!-- Challenge Types Grid -->
        <div class="types-grid">
            {#each challengeTypes as type}
                <div class="type-card" class:highlight={type.name === '추격전'}>
                    <span class="type-icon">{type.icon}</span>
                    <span class="type-name">{type.name}</span>
                    <span class="type-desc">{type.desc}</span>
                </div>
            {/each}
        </div>
    </div>

    <!-- 2-2: 추격 모드 (Chase Mode) — 별도 강조 -->
    <div class="chase-block">
        <div class="chase-inner">
            <h3 class="chase-title">추격 모드 — 도망쳐! 잡아!</h3>
            <p class="chase-desc">
                운동이 지루하다고요?<br />
                도망자 vs 추격자로 나뉘어 실제로 뛰면서 하는 건강 게임!<br />
                감옥과 구출까지 — 이건 운동 앱이 아니라 건강 모험입니다.
            </p>

            <!-- Role Cards -->
            <div class="role-cards">
                <div class="role-card runner">
                    <div class="role-header">
                        <span class="role-icon">{'\uD83C\uDFC3'}</span>
                        <span class="role-name">도망자 (Runner)</span>
                    </div>
                    <ul class="role-list">
                        <li><strong>목표:</strong> 제한 시간까지 생존</li>
                        <li><strong>전략:</strong> 도망 + 감옥 모드에서 팀원 구출</li>
                    </ul>
                </div>
                <div class="role-card chaser">
                    <div class="role-header">
                        <span class="role-icon">{'\uD83D\uDD0D'}</span>
                        <span class="role-name">추격자 (Chaser)</span>
                    </div>
                    <ul class="role-list">
                        <li><strong>목표:</strong> 모든 도망자 포획</li>
                        <li><strong>특수기능:</strong> 포획 (가까이 가면 켜지는 버튼 탭)</li>
                        <li><strong>전략:</strong> 추격 + 감옥 모드에서는 잡은 도망자 감옥행</li>
                    </ul>
                </div>
            </div>

            <!-- Difficulty Presets -->
            <div class="difficulties">
                <h4 class="difficulties-title">난이도 프리셋</h4>
                <div class="difficulty-grid">
                    {#each difficulties as diff}
                        <div class="difficulty-card">
                            <div class="difficulty-dot" style="background: {diff.color}"></div>
                            <div class="difficulty-name">{diff.name}</div>
                            <div class="difficulty-desc">{diff.desc}</div>
                            <div class="difficulty-features">
                                <span class:enabled={diff.prison}>감옥</span>
                                <span class:enabled={diff.steps}>걸음수 목표</span>
                            </div>
                        </div>
                    {/each}
                </div>
            </div>

            <!-- Bottom Tags -->
            <div class="chase-tags">
                <span class="chase-tag">Android &harr; iOS 크로스플랫폼 지원</span>
                <span class="chase-tag">스마트워치 연동</span>
                <span class="chase-tag">특허 기술 기반</span>
            </div>

            <!-- 서버 렌더 아티클로 가는 진짜 앵커. SPA_ROUTES 밖이라 풀 네비게이션된다. -->
            <div class="chase-more">
                <a href="/guide/nearby-challenge-guide" class="chase-more-link">
                    근거리 챌린지와 추격전 규칙 자세히 보기
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                    </svg>
                </a>
                <a href="/guide/gyeongdo-guide" class="chase-more-link">
                    경찰과 도둑 룰 자세히 보기
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                    </svg>
                </a>
            </div>
        </div>
    </div>

</section>

<style>
    .diff-section {
        padding: 5rem 1.5rem;
        background: var(--landing-bg, #FFFFFF);
    }

    /* Header */
    .diff-header {
        text-align: center;
        max-width: 42rem;
        margin: 0 auto 4rem;
    }

    .diff-title {
        font-size: clamp(1.8rem, 4vw, 2.5rem);
        font-weight: 700;
        color: var(--landing-text, #1A1A1A);
        margin: 0 0 1.25rem;
        letter-spacing: -0.02em;
    }

    .diff-subtitle {
        font-size: 1rem;
        color: var(--landing-text-sub, #666666);
        line-height: 1.8;
    }

    .diff-subtitle strong {
        color: var(--landing-primary, #1E5C3A);
    }

    /* Block */
    .diff-block {
        max-width: 64rem;
        margin: 0 auto 4rem;
    }

    .block-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--landing-text, #1A1A1A);
        margin: 0 0 0.75rem;
        text-align: center;
    }

    .block-desc {
        font-size: 0.95rem;
        color: var(--landing-text-sub, #666666);
        line-height: 1.7;
        text-align: center;
        max-width: 36rem;
        margin: 0 auto 2.5rem;
    }

    /* App Screenshots */
    .diff-screenshots {
        display: flex;
        justify-content: center;
        gap: 1.5rem;
        margin-bottom: 3rem;
    }

    .diff-phone {
        width: 160px;
        background: #1a1a1a;
        border-radius: 24px;
        padding: 8px;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
        transition: transform 0.3s;
    }

    .diff-phone:hover {
        transform: translateY(-4px);
    }

    .diff-phone img {
        width: 100%;
        height: auto;
        border-radius: 18px;
        display: block;
    }

    /* Flow Steps */
    .flow-steps {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 3rem;
    }

    .flow-step {
        background: var(--landing-bg-alt, #F8F9FA);
        border: 1px solid rgba(30, 92, 58, 0.1);
        border-radius: 12px;
        padding: 1rem 1.5rem;
        text-align: center;
        width: 100%;
        max-width: 260px;
    }

    .flow-number {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--landing-primary, #1E5C3A);
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
    }

    .flow-title {
        font-size: 0.9rem;
        font-weight: 600;
        color: var(--landing-text, #1A1A1A);
        margin-bottom: 0.25rem;
    }

    .flow-desc {
        font-size: 0.78rem;
        color: var(--landing-text-sub, #666666);
    }

    .flow-arrow {
        color: var(--landing-primary, #1E5C3A);
        opacity: 0.4;
        transform: rotate(90deg);
    }

    /* Challenge Types Grid */
    .types-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.75rem;
    }

    .type-card {
        background: var(--landing-bg-alt, #F8F9FA);
        border: 1px solid rgba(30, 92, 58, 0.08);
        border-radius: 12px;
        padding: 1rem;
        text-align: center;
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
        transition: all 0.2s;
    }

    .type-card:hover {
        border-color: rgba(30, 92, 58, 0.2);
        transform: translateY(-2px);
    }

    .type-card.highlight {
        background: rgba(255, 107, 53, 0.08);
        border-color: rgba(255, 107, 53, 0.3);
    }

    .type-icon {
        font-size: 1.5rem;
    }

    .type-name {
        font-size: 0.82rem;
        font-weight: 600;
        color: var(--landing-text, #1A1A1A);
    }

    .type-desc {
        font-size: 0.72rem;
        color: var(--landing-text-sub, #666666);
    }

    /* Chase Mode Block */
    .chase-block {
        background: var(--landing-primary, #1E5C3A);
        border-radius: 24px;
        margin: 0 auto 4rem;
        max-width: 64rem;
        overflow: hidden;
    }

    .chase-inner {
        padding: 3rem 1.5rem;
        color: #fff;
    }

    .chase-title {
        font-size: clamp(1.5rem, 3.5vw, 2rem);
        font-weight: 700;
        margin: 0 0 1rem;
        text-align: center;
    }

    .chase-desc {
        font-size: 0.95rem;
        line-height: 1.8;
        color: rgba(255, 255, 255, 0.75);
        text-align: center;
        max-width: 32rem;
        margin: 0 auto 2.5rem;
    }

    /* Role Cards */
    .role-cards {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
        margin-bottom: 2rem;
    }

    .role-card {
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.15);
        border-radius: 16px;
        padding: 1.5rem;
    }

    .role-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.75rem;
    }

    .role-icon {
        font-size: 1.3rem;
    }

    .role-name {
        font-size: 1.05rem;
        font-weight: 600;
    }

    .role-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.4rem;
    }

    .role-list li {
        font-size: 0.85rem;
        color: rgba(255, 255, 255, 0.8);
        line-height: 1.5;
    }

    /* Difficulties */
    .difficulties {
        margin-bottom: 2rem;
    }

    .difficulties-title {
        font-size: 1rem;
        font-weight: 600;
        text-align: center;
        margin-bottom: 1rem;
        color: rgba(255, 255, 255, 0.9);
    }

    .difficulty-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.75rem;
    }

    .difficulty-card {
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        padding: 1rem;
        text-align: center;
    }

    .difficulty-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        display: inline-block;
        margin-bottom: 0.4rem;
    }

    .difficulty-name {
        font-size: 0.85rem;
        font-weight: 600;
        margin-bottom: 0.2rem;
    }

    .difficulty-desc {
        font-size: 0.72rem;
        color: rgba(255, 255, 255, 0.6);
        margin-bottom: 0.5rem;
    }

    .difficulty-features {
        display: flex;
        flex-wrap: wrap;
        gap: 0.3rem;
        justify-content: center;
    }

    .difficulty-features span {
        font-size: 0.65rem;
        padding: 0.15rem 0.4rem;
        border-radius: 4px;
        background: rgba(255, 255, 255, 0.08);
        color: rgba(255, 255, 255, 0.4);
    }

    .difficulty-features span.enabled {
        background: rgba(255, 255, 255, 0.2);
        color: rgba(255, 255, 255, 0.9);
    }

    /* Chase Tags */
    .chase-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        justify-content: center;
    }

    .chase-tag {
        font-size: 0.8rem;
        padding: 0.4rem 0.8rem;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 100px;
        color: rgba(255, 255, 255, 0.85);
    }

    .chase-more {
        margin-top: 1.75rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.65rem;
    }

    .chase-more-link {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        font-size: 0.95rem;
        font-weight: 600;
        color: #fff;
        text-decoration: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.45);
        padding-bottom: 0.15rem;
        transition: border-color 0.2s;
    }

    .chase-more-link:hover {
        border-bottom-color: #fff;
    }

    /* Responsive */
    @media (min-width: 768px) {
        .diff-section {
            padding: 6rem 2.5rem;
        }

        .diff-phone {
            width: 200px;
            border-radius: 28px;
            padding: 10px;
        }

        .diff-phone img {
            border-radius: 20px;
        }

        .flow-steps {
            flex-direction: row;
            justify-content: center;
        }

        .flow-step {
            width: auto;
            max-width: 160px;
        }

        .flow-arrow {
            transform: none;
        }

        .role-cards {
            grid-template-columns: 1fr 1fr;
        }

        .chase-inner {
            padding: 4rem 3rem;
        }

        .difficulty-grid {
            grid-template-columns: repeat(4, 1fr);
        }
    }

    @media (min-width: 1024px) {
        .diff-section {
            padding: 7rem 3rem;
        }

        .types-grid {
            grid-template-columns: repeat(5, 1fr);
        }
    }
</style>
